// NotificationPrompt — paper-style modal overlay 攔在 Home 之上、確保
// 學員一進站就被引導去啟用通知。
//
// 設計選擇:
//  - 不像 InstallPrompt 那樣接管整個 PWA route、改成浮在 Home 之上的卡,
//    保持「同款 PWA 內部、做一個決定」的氛圍、不會像被踢出 Home。
//  - 「· 啟 用 通 知」是真實的 user gesture、按下後直接呼叫
//    requestPermission() 跳系統權限框(或在 permission 已 granted 但
//    isFresh=false 時、跑 force re-subscribe)。
//  - 「· 稍 後 再 試」每次進站還是會彈、配合「每次進站都彈直到啟用」策略。
//
// 視覺對齊 DESIGN.md:純灰階、直角、1px hairline、Han / Mono 字體。

import { Hairline, Han, MonoLabel, T, TextBtn } from "@goodbye/ui";

export interface NotificationPromptProps {
  /** Browser-level permission. denied 時不顯示「啟用」按鈕、改顯示
   *  「請至系統設定開啟通知」提示。 */
  permission: "default" | "granted" | "denied" | "unsupported";
  /** 使用者按「· 啟 用 通 知」時觸發。Home 那邊會分流:
   *   permission === "default" → requestPushPermission()
   *   permission === "granted" 但 isFresh=false → subscribePushNow() */
  onEnable: () => void | Promise<void>;
  /** 「· 稍 後 再 試」— 關閉這次 modal、不蓋 localStorage 旗標、
   *  下次進站還會再彈。 */
  onDismiss: () => void;
}

export function NotificationPrompt({
  permission,
  onEnable,
  onDismiss,
}: NotificationPromptProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="啟用通知"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(14, 14, 14, 0.45)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        animation: "gb-fade-in 320ms var(--ease-mist)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 360,
          background: T.paper,
          border: `1px solid ${T.ink200}`,
          padding: "28px 28px 22px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          animation: "gb-toast-rise 480ms var(--ease-mist)",
        }}
      >
        <MonoLabel size={10} color={T.ink500}>· 通 知</MonoLabel>
        <Han size={20} color={T.ink900} lh={1.5}>
          兩天裡、<br />這支 app 會說話。
        </Han>
        <Han size={13} color={T.ink500} lh={1.8}>
          開啟通知、它才接得到。
        </Han>

        <Hairline />

        {permission === "denied" && (
          <Han size={12} color={T.ink700} lh={1.7}>
            · 你之前拒絕了通知權限、需要到手機系統「設定 → 通知 → 再見」
            裡手動打開。
          </Han>
        )}

        {permission === "unsupported" && (
          <Han size={12} color={T.ink700} lh={1.7}>
            · 此裝置不支援通知、請先將 app 加到主畫面、再從主畫面打開。
          </Han>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 6,
          }}
        >
          <TextBtn
            onClick={onDismiss}
            mono
            size={11}
            ls="0.2em"
            color={T.ink350}
            framed={false}
            style={{ padding: "8px 0", minHeight: 0, minWidth: 0 }}
          >
            · 稍 後 再 試
          </TextBtn>

          {(permission === "default" || permission === "granted") && (
            <TextBtn
              onClick={() => {
                void onEnable();
              }}
              mono={false}
              size={15}
              ls="0.3em"
              color={T.ink900}
              framed
              style={{ padding: "10px 16px" }}
            >
              · 啟 用 通 知
            </TextBtn>
          )}
        </div>
      </div>
    </div>
  );
}
